import * as dotProp from 'dot-prop-immutable';
import { Action } from 'typescript-fsa';
import { insert, remove } from 'ramda';

import { createReducer, generateUUID } from '@topconagriculture/frontend-utils';

import {
  addIngredientInPremix,
  addIngredientInPremixSilent,
  deleteIngredientInPremix,
  emptyIngredientsInPremix,
  previewCreatePremix,
  setActualLoadTargetWeight,
  setAutoAdjWater,
  setDryMatterTargetWeight,
  setEditModeInPremixCreation,
  setEditPremix,
  setMethod,
  setSelectedIngredientInList,
  setTargetWeightType,
  setWaterAmount,
  updateActualLoad,
  updateActualLoadPercentage,
  updateDryMatter,
  updateDryMatterPercentage,
  updateIngredientOrderInPremix,
  updateMixerData,
} from './appPremixes.actions';
import { PremixState } from '../../entities/premixes/premixes.reducer';
import { IngredientInRation, PreviewPremix } from '../../entities/premixes/premixes.types';
import {
  AddIngredientPayload,
  EntryMethodsKeys,
  IngredientInPremixCreation,
  TargetWeights,
} from '../recipes/appRecipes.types';
// tslint:disable-next-line

export type AppPremixState = {
  readonly premixCreation: {
    readonly ingredientsInPremix: IngredientInPremixCreation[];
    readonly previewPremix: PreviewPremix | null;
    readonly isEditMode: boolean;
    readonly autoAdjWater: boolean;
    readonly method: EntryMethodsKeys;
    readonly targetWeightType: TargetWeights;
    readonly targetActualLoadWeight: number;
    readonly targetDryMatterWeight: number;
    readonly finalDryMatterPercentage: number;
    readonly selectedIngredientInList: string | null;
  };
  readonly premixPayload: {
    readonly ingredients: IngredientInRation[];
  };
};

export const initialState: AppPremixState = {
  premixCreation: {
    ingredientsInPremix: [],
    previewPremix: null,
    isEditMode: false,
    autoAdjWater: false,
    targetWeightType: TargetWeights.DRY,
    method: EntryMethodsKeys.WEIGHT,
    targetActualLoadWeight: 0,
    targetDryMatterWeight: 0,
    finalDryMatterPercentage: 0,
    selectedIngredientInList: null,
  },
  premixPayload: {
    ingredients: [],
  },
};

const addIngredientInPremixReducer = (state: AppPremixState, action: Action<AddIngredientPayload>) => {
  const existingIngredient = state.premixCreation.ingredientsInPremix.find((ing) => ing.id === action.payload.id);
  const ingredientsInPremix = [...state.premixCreation.ingredientsInPremix];
  const { autoAdjWater } = state.premixCreation;

  if (existingIngredient && action.payload.isOverridable) {
    const overridableItemIndex = state.premixCreation.ingredientsInPremix.findIndex(
      (ing) => ing.id === action.payload.id,
    );

    ingredientsInPremix[overridableItemIndex] = {
      ...existingIngredient,
      actualLoad: existingIngredient.actualLoad + action.payload.actualLoad! || 0,
    };
  }

  const waterWeight = ingredientsInPremix.reduce((acc, ing) => acc + (ing.dryMatterPerc === 0 ? ing.actualLoad : 0), 0);
  const waterIngredientsCount = ingredientsInPremix.filter((ing) => ing.dryMatterPerc === 0).length + 1;
  const actualLoad = ingredientsInPremix.reduce((acc, ing) => acc + ing.actualLoad, 0);
  const isWater = action.payload.dryMatterPerc === 0;

  const ingredients =
    existingIngredient && action.payload.isOverridable
      ? [...ingredientsInPremix]
      : [
          // Include ingredient
          ...state.premixCreation.ingredientsInPremix.map((elem) => {
            return {
              ...elem,
              actualLoad:
                elem.dryMatterPerc === 0 && autoAdjWater && isWater
                  ? waterWeight / waterIngredientsCount
                  : elem.actualLoad || 0,
              actualLoadTotalPercentage:
                elem.dryMatterPerc === 0 && autoAdjWater && isWater
                  ? (waterWeight / waterIngredientsCount / actualLoad) * 100
                  : elem.actualLoadTotalPercentage,
            };
          }),
          {
            id: action.payload.id,
            uuid: action.payload.listId || generateUUID(),
            actualLoad:
              action.payload.dryMatterPerc === 0 && autoAdjWater
                ? waterWeight / waterIngredientsCount
                : action.payload.actualLoad || 0,
            dryMatter: action.payload.dryMatter || 0,
            dryMatterPerc: action.payload.dryMatterPerc || 0,
            actualLoadTotalPercentage:
              action.payload.dryMatterPerc === 0 && autoAdjWater
                ? (waterWeight / waterIngredientsCount / actualLoad) * 100
                : 0,
            dryMatterTotalPercentage: 0,
            mixerData: {
              mixingTypeCode: (action.payload.mixerData && action.payload.mixerData.mixingTypeCode) || 0,
              mixingValue: (action.payload.mixerData && action.payload.mixerData.mixingValue) || 0,
              mixingStartTypeCode: (action.payload.mixerData && action.payload.mixerData.mixingStartTypeCode) || 1,
            },
            toleranceWeight: action.payload.toleranceWeight,
            toleranceMotion: action.payload.toleranceMotion,
          },
        ];

  return {
    ...state,
    premixCreation: {
      ...state.premixCreation,
      ingredientsInPremix: ingredients,
      targetDryMatterWeight: state.premixCreation.targetDryMatterWeight + ingredients[ingredients.length - 1].dryMatter,
      targetActualLoadWeight:
        state.premixCreation.targetActualLoadWeight + ingredients[ingredients.length - 1].actualLoad,
    },
  };
};

const appPremixReducer = createReducer(initialState, {
  [setEditModeInPremixCreation.type]: (state: AppPremixState, action: Action<boolean>) => {
    return dotProp.set(state, 'premixCreation.isEditMode', action.payload);
  },
  [setAutoAdjWater.type]: (state: AppPremixState, action: Action<boolean>) => {
    return dotProp.set(state, 'premixCreation.autoAdjWater', action.payload);
  },
  [setMethod.type]: (state: AppPremixState, action: Action<EntryMethodsKeys>) => {
    return dotProp.set(state, 'premixCreation.method', action.payload);
  },
  [setTargetWeightType.type]: (state: AppPremixState, action: Action<TargetWeights>) => {
    return dotProp.set(state, 'premixCreation.targetWeightType', action.payload);
  },
  [updateMixerData.type]: (state: AppPremixState, action: ReturnType<typeof updateMixerData>) => {
    const { payload } = action;
    const { ingredientsInPremix } = state.premixCreation;
    const ingredients = ingredientsInPremix.map((ingredient) => {
      const isCurrentItem = ingredient.uuid === payload.listId;
      return isCurrentItem
        ? {
            ...ingredient,
            mixerData: { ...ingredient.mixerData, ...payload.mixerData },
          }
        : ingredient;
    });
    return dotProp.set(state, 'premixCreation.ingredientsInPremix', ingredients);
  },
  [updateActualLoad.type]: (state: AppPremixState, action: ReturnType<typeof updateActualLoad>) => {
    const { payload } = action;
    const { ingredientsInPremix } = state.premixCreation;
    const ingredients = ingredientsInPremix.map((ingredient) => {
      const isCurrentItem = ingredient.uuid === payload.listId;
      return isCurrentItem
        ? {
            ...ingredient,
            actualLoad: payload.actualLoad,
          }
        : ingredient;
    });
    const newState = {
      premixCreation: {
        ...state.premixCreation,
        ingredientsInPremix: ingredients,
      },
      premixPayload: {
        ...state.premixPayload,
        ingredients: ingredientsInPremix.map((ingredient) => {
          const isCurrentItem = ingredient.uuid === payload.listId;
          return {
            id: ingredient.ownId,
            ingredientId: ingredient.id,
            amounts: isCurrentItem ? [payload.actualLoad] : [ingredient.actualLoad],
          };
        }),
      },
    };
    return newState;
  },
  [updateActualLoadPercentage.type]: (state: AppPremixState, action: ReturnType<typeof updateActualLoadPercentage>) => {
    const { payload } = action;
    const { ingredientsInPremix } = state.premixCreation;
    const ingredients = ingredientsInPremix.map((ingredient) => {
      const isCurrentItem = ingredient.uuid === payload.listId;
      return isCurrentItem
        ? {
            ...ingredient,
            actualLoadTotalPercentage: payload.actualLoadPercentage,
          }
        : ingredient;
    });
    const newState = {
      premixCreation: {
        ...state.premixCreation,
        ingredientsInPremix: ingredients,
      },
      premixPayload: {
        ...state.premixPayload,
        amounts: [state.premixCreation.targetActualLoadWeight],
        ingredients: ingredientsInPremix.map((ingredient) => {
          const isCurrentItem = ingredient.uuid === payload.listId;
          return {
            id: ingredient.ownId,
            ingredientId: ingredient.id,
            loadPercs: isCurrentItem ? [payload.actualLoadPercentage] : [ingredient.actualLoadTotalPercentage],
          };
        }),
      },
    };
    return newState;
  },
  [updateDryMatter.type]: (state: AppPremixState, action: ReturnType<typeof updateDryMatter>) => {
    const { payload } = action;
    const { ingredientsInPremix } = state.premixCreation;
    const ingredients = ingredientsInPremix.map((ingredient) => {
      const isCurrentItem = ingredient.uuid === payload.listId;
      return isCurrentItem
        ? {
            ...ingredient,
            dryMatter: payload.dryMatter,
          }
        : ingredient;
    });
    const newState = {
      premixCreation: {
        ...state.premixCreation,
        ingredientsInPremix: ingredients,
      },
      premixPayload: {
        ...state.premixPayload,
        ingredients: ingredientsInPremix.map((ingredient) => {
          const isCurrentItem = ingredient.uuid === payload.listId;
          const isWater = isWaterIngredient(ingredient.dryMatterPerc);
          return {
            id: ingredient.ownId,
            ingredientId: ingredient.id,
            dryAmounts: isWater ? undefined : isCurrentItem ? [payload.dryMatter] : [ingredient.dryMatter],
            amounts: isWater ? [ingredient.actualLoad] : undefined,
          };
        }),
      },
    };
    return newState;
  },
  [updateDryMatterPercentage.type]: (state: AppPremixState, action: ReturnType<typeof updateDryMatterPercentage>) => {
    const { payload } = action;
    const { ingredientsInPremix } = state.premixCreation;
    const ingredients = ingredientsInPremix.map((ingredient) => {
      const isCurrentItem = ingredient.uuid === payload.listId;
      return isCurrentItem
        ? {
            ...ingredient,
            dryMatterPercentage: payload.dryMatterPercentage,
          }
        : ingredient;
    });
    const newState = {
      premixCreation: {
        ...state.premixCreation,
        ingredientsInPremix: ingredients,
      },
      premixPayload: {
        ...state.premixPayload,
        dryAmounts: [state.premixCreation.targetDryMatterWeight],
        ingredients: ingredientsInPremix.map((ingredient) => {
          const isCurrentItem = ingredient.uuid === payload.listId;
          const isWater = isWaterIngredient(ingredient.dryMatterPerc);
          return {
            id: ingredient.ownId,
            ingredientId: ingredient.id,
            refusal: ingredient.refusal,
            refusalSubstitute: ingredient.refusalSubstitute,
            dryLoadPercs: isWater
              ? undefined
              : isCurrentItem
              ? [payload.dryMatterPercentage]
              : [ingredient.dryMatterTotalPercentage],
            loadPercs: isWater ? [ingredient.actualLoadTotalPercentage] : undefined,
          };
        }),
      },
    };
    return newState;
  },
  [setEditPremix.type]: (state: AppPremixState, action: ReturnType<typeof setEditPremix>) => {
    const { ingredients, amounts, dryAmounts, percEntryMethod } = action.payload;
    const { method } = state.premixCreation;
    const ingredientsInPremix = ingredients.map((elem) => ({
      id: elem.ingredientId,
      ownId: elem.id,
      uuid: generateUUID(),
      actualLoad: elem.amounts && elem.amounts[0],
      dryMatter: elem.dryAmounts && elem.dryAmounts[0],
      actualLoadTotalPercentage: elem.loadPercs && elem.loadPercs[0],
      dryMatterTotalPercentage: elem.dryLoadPercs && elem.dryLoadPercs[0],
      dryMatterPerc: elem.dryMatterPerc,
      mixerData: {
        mixingTypeCode: elem.mixingTypeCode || 0,
        mixingValue: elem.mixingValue || 0,
        mixingStartTypeCode: elem.mixingStartTypeCode || 1,
      },
      refusal: elem.refusal,
      refusalSubstitute: elem.refusalSubstitute,
      toleranceMotion: elem.toleranceMotion || 0,
      toleranceWeight: elem.toleranceWeight || 0,
    }));
    const premixCreation = {
      ...state.premixCreation,
      targetActualLoadWeight: amounts && amounts![0],
      targetDryMatterWeight: dryAmounts && dryAmounts![0],
      ingredientsInPremix,
      previewPremix: action.payload,
    };
    const premixPayload = {
      ...state.premixPayload,
      amounts:
        method === EntryMethodsKeys.PERCENTAGE && percEntryMethod === TargetWeights.ACTUAL && amounts
          ? [amounts[0]]
          : undefined,
      dryAmounts:
        method === EntryMethodsKeys.PERCENTAGE && percEntryMethod === TargetWeights.DRY && dryAmounts
          ? [dryAmounts[0]]
          : undefined,
      ingredients: ingredientsInPremix.map((ingredient) => ({
        id: ingredient.ownId,
        ingredientId: ingredient.id,
        amounts: [ingredient.actualLoad],
      })),
    };
    return {
      premixCreation,
      premixPayload,
    };
  },
  [setWaterAmount.type]: (state: AppPremixState, action: Action<number>) => {
    const { method, targetWeightType } = state.premixCreation;
    return dotProp.set(state, 'premixPayload', {
      ...state.premixPayload,
      ingredients: state.premixCreation.ingredientsInPremix.map((ingredient) => {
        const isWater = isWaterIngredient(ingredient.dryMatterPerc);
        return {
          id: ingredient.ownId,
          ingredientId: ingredient.id,
          refusal: ingredient.refusal,
          refusalSubstitute: ingredient.refusalSubstitute,
          amounts: method === EntryMethodsKeys.WEIGHT ? [ingredient.actualLoad] : undefined,
          loadPercs:
            method === EntryMethodsKeys.PERCENTAGE && (targetWeightType === TargetWeights.ACTUAL || isWater)
              ? [ingredient.actualLoadTotalPercentage]
              : undefined,
          dryLoadPercs:
            method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.DRY
              ? [ingredient.dryMatterTotalPercentage]
              : undefined,
        };
      }),
      targetDMPerc: action.payload,
    });
  },
  [setActualLoadTargetWeight.type]: (state: AppPremixState, action: Action<typeof setActualLoadTargetWeight>) => {
    const { payload } = action;
    const { ingredientsInPremix } = state.premixCreation;
    const newState = {
      premixCreation: {
        ...state.premixCreation,
        targetActualLoadWeight: payload,
        targetWeightType: TargetWeights.ACTUAL,
      },
      premixPayload: {
        amounts: [payload],
        ingredients: ingredientsInPremix.map((ingredient) => ({
          id: ingredient.ownId,
          ingredientId: ingredient.id,
          loadPercs: [ingredient.actualLoadTotalPercentage],
        })),
      },
    };
    return newState;
  },
  [setDryMatterTargetWeight.type]: (state: AppPremixState, action: Action<typeof setDryMatterTargetWeight>) => {
    const { payload } = action;
    const { ingredientsInPremix } = state.premixCreation;
    const newState = {
      premixCreation: {
        ...state.premixCreation,
        targetDryMatterWeight: payload,
        targetWeightType: TargetWeights.DRY,
      },
      premixPayload: {
        dryAmounts: [payload],
        ingredients: ingredientsInPremix.map((ingredient) => {
          const isWater = isWaterIngredient(ingredient.dryMatterPerc);
          return {
            id: ingredient.ownId,
            ingredientId: ingredient.id,
            loadPercs: isWater ? [ingredient.actualLoadTotalPercentage] : undefined,
            dryLoadPercs: !isWater ? [ingredient.dryMatterTotalPercentage] : undefined,
          };
        }),
      },
    };
    return newState;
  },
  [setSelectedIngredientInList.type]: (state: AppPremixState, action: Action<string | null>) => {
    return dotProp.set(state, 'premixCreation.selectedIngredientInList', action.payload);
  },
  [addIngredientInPremix.type]: addIngredientInPremixReducer,
  [addIngredientInPremixSilent.type]: addIngredientInPremixReducer,
  [updateIngredientOrderInPremix.type]: (
    state: AppPremixState,
    action: Action<{ newPosition: number; uuid: string }>,
  ) => {
    const movedObject = action.payload;
    const currentArray = state.premixCreation.ingredientsInPremix;
    const currentItem = currentArray.find((ingredient) => ingredient.uuid === movedObject.uuid);
    const findIndex = currentArray.findIndex((currentIndex) => currentIndex.uuid === movedObject.uuid);
    const slicedArray = remove(findIndex, 1, currentArray);
    const newArray = insert(movedObject.newPosition, currentItem, slicedArray);
    return dotProp.set(state, 'premixCreation.ingredientsInPremix', newArray);
  },
  [deleteIngredientInPremix.type]: (state: AppPremixState, action: Action<{ id: string }>) => {
    const { ingredientsInPremix } = state.premixCreation;
    const newIngredients = ingredientsInPremix.filter((ingredient) => ingredient.uuid !== action.payload.id);

    const newState = {
      premixCreation: {
        ...state.premixCreation,
        ingredientsInPremix: newIngredients,
      },
      premixPayload: {
        ...state.premixPayload,
        ingredients: newIngredients.map((ingredient) => ({
          id: ingredient.ownId,
          ingredientId: ingredient.id,
          amounts: [ingredient.actualLoad],
        })),
      },
    };

    return newState;
  },
  [emptyIngredientsInPremix.type]: (state: AppPremixState) => ({
    ...state,
    premixCreation: {
      ...state.premixCreation,
      previewPremix: null,
      autoAdjWater: false,
      ingredientsInPremix: [],
      targetActualLoadWeight: 0,
      targetDryMatterWeight: 0,
      finalDryMatterPercentage: 0,
    },
    premixPayload: {
      ingredients: [],
    },
  }),
  [previewCreatePremix.done.type]: (state: AppPremixState, action: Action<{ result: PreviewPremix }>) => {
    const { ingredients, amounts, dryAmounts } = action.payload.result;
    const premixCreation = {
      ...state.premixCreation,
      targetActualLoadWeight: amounts && amounts[0],
      targetDryMatterWeight: dryAmounts && dryAmounts![0],
      ingredientsInPremix: state.premixCreation.ingredientsInPremix.map((ingredient, index) => ({
        ...ingredient,
        ownId: ingredients[index].id,
        actualLoad: ingredients && ingredients[index].amounts ? ingredients[index].amounts[0] : 0,
        dryMatter: ingredients && ingredients[index].dryAmounts ? ingredients[index].dryAmounts[0] : 0,
        actualLoadTotalPercentage: ingredients && ingredients[index].loadPercs ? ingredients[index].loadPercs[0] : 0,
        dryMatterTotalPercentage:
          ingredients && ingredients[index].dryLoadPercs ? ingredients[index].dryLoadPercs[0] : 0,
        dryMatterPerc: state.premixCreation.ingredientsInPremix[index].dryMatterPerc,
        mixerData: {
          mixingValue: ingredients && ingredients[index].mixingValue,
          mixingTypeCode: ingredients && ingredients[index].mixingTypeCode,
          mixingStartTypeCode: ingredients && ingredients[index].mixingStartTypeCode,
          mixingRPM: ingredients && ingredients[index].mixingRPM,
          mixingStartValue: ingredients && ingredients[index].mixingStartValue,
        },
      })),
      previewPremix: action.payload.result,
    };
    return dotProp.set(state, 'premixCreation', premixCreation);
  },
  [previewCreatePremix.failed.type]: (state: PremixState) => dotProp.set(state, 'premixCreation.previewPremix', null),
});

const isWaterIngredient = (dryMatterPerc: number) => dryMatterPerc === 0;

export default appPremixReducer;
